// client/src/components/analytics/PostAnalytics.jsx
// Per-post analytics list with sorting and filtering

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchPostAnalytics, formatMetricNumber, PLATFORM_META } from '../../services/analyticsApi';
import AppFooter from '../AppFooter';
import { FaArrowLeft, FaHome } from 'react-icons/fa';

const SORT_OPTIONS = [
  { value: 'date', label: 'Date' },
  { value: 'engagement', label: 'Engagement' },
  { value: 'reach', label: 'Reach' },
  { value: 'impressions', label: 'Impressions' },
  { value: 'ctr', label: 'CTR' },
];

function PostAnalytics() {
  const { tenant } = useParams();
  const navigate = useNavigate();
  const tenantId = Number(tenant);

  const [days, setDays] = useState(30);
  const [platform, setPlatform] = useState('');
  const [sort, setSort] = useState('date');
  const [posts, setPosts] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, hasMore: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);
  const LIMIT = 20;

  const loadPosts = useCallback(async (appendMode = false) => {
    if (!tenantId) return;
    setLoading(true);
    setError(null);

    try {
      const currentOffset = appendMode ? offset : 0;
      const data = await fetchPostAnalytics(tenantId, {
        days,
        platform: platform || undefined,
        sort,
        limit: LIMIT,
        offset: currentOffset,
      });
      if (appendMode) {
        setPosts(prev => [...prev, ...data.posts]);
      } else {
        setPosts(data.posts || []);
      }
      setPagination(data.pagination || { total: 0, hasMore: false });
      setOffset(currentOffset + LIMIT);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tenantId, days, platform, sort, offset]);

  useEffect(() => {
    setOffset(0);
    loadPosts(false);
  }, [tenantId, days, platform, sort]);

  return (
    <div style={{ paddingBottom: 80 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <h2 style={{ margin: 0, fontSize: '1.3rem', fontWeight: 600 }}>Post Analytics</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select className="form-select form-select-sm" value={days} onChange={(e) => setDays(Number(e.target.value))} style={{ width: 'auto' }}>
            <option value={7}>7 Days</option>
            <option value={14}>14 Days</option>
            <option value={30}>30 Days</option>
            <option value={60}>60 Days</option>
            <option value={90}>90 Days</option>
          </select>
          <select className="form-select form-select-sm" value={platform} onChange={(e) => setPlatform(e.target.value)} style={{ width: 'auto' }}>
            <option value="">All Platforms</option>
            {Object.entries(PLATFORM_META).map(([key, meta]) => (
              <option key={key} value={key}>{meta.name}</option>
            ))}
          </select>
          <select className="form-select form-select-sm" value={sort} onChange={(e) => setSort(e.target.value)} style={{ width: 'auto' }}>
            {SORT_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>Sort: {o.label}</option>
            ))}
          </select>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {loading && posts.length === 0 ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
          <p className="mt-2 text-muted">Loading post analytics...</p>
        </div>
      ) : posts.length === 0 ? (
        <div className="text-center py-5 text-muted">
          <i className="bi bi-bar-chart" style={{ fontSize: '3rem' }}></i>
          <h4 className="mt-3">No Post Data</h4>
          <p>No published posts with metrics found for this period.</p>
        </div>
      ) : (
        <>
          <p className="text-muted small mb-3">
            Showing {posts.length} of {pagination.total} posts
          </p>

          <div className="list-group mb-3">
            {posts.map((post, i) => {
              const meta = PLATFORM_META[post.metricPlatform] || { name: post.metricPlatform || 'Unknown', color: '#666', icon: 'bi-globe' };
              return (
                <div key={`${post.postId}-${post.metricPlatform}-${i}`} className="list-group-item">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="d-flex align-items-center gap-2 mb-1">
                        <i className={`bi ${meta.icon}`} style={{ color: meta.color }}></i>
                        <span className="badge bg-light text-dark" style={{ fontSize: '0.7rem' }}>{meta.name}</span>
                        {post.platforms?.length > 1 && (
                          <span className="badge bg-secondary" style={{ fontSize: '0.65rem' }}>
                            +{post.platforms.length - 1} platform{post.platforms.length > 2 ? 's' : ''}
                          </span>
                        )}
                        <small className="text-muted ms-auto">
                          {post.postedOn ? new Date(post.postedOn).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
                        </small>
                      </div>
                      <p className="mb-2" style={{ fontSize: '0.85rem', lineHeight: 1.4 }}>
                        {(post.content || '').substring(0, 150) || '(No text)'}
                        {post.content?.length > 150 ? '...' : ''}
                      </p>
                    </div>
                  </div>

                  {/* Metrics row */}
                  <div className="row g-2">
                    <MetricBadge icon="bi-heart-fill" color="#e91e63" label="Likes" value={post.likes} />
                    <MetricBadge icon="bi-chat-fill" color="#2196f3" label="Comments" value={post.comments} />
                    <MetricBadge icon="bi-share-fill" color="#ff9800" label="Shares" value={post.shares} />
                    {post.saves > 0 && <MetricBadge icon="bi-bookmark-fill" color="#9c27b0" label="Saves" value={post.saves} />}
                    {post.reach > 0 && <MetricBadge icon="bi-eye-fill" color="#607d8b" label="Reach" value={post.reach} />}
                    {post.impressions > 0 && <MetricBadge icon="bi-eye" color="#795548" label="Impressions" value={post.impressions} />}
                    {post.videoViews > 0 && <MetricBadge icon="bi-play-circle-fill" color="#f44336" label="Views" value={post.videoViews} />}
                  </div>

                  {/* KPI badges */}
                  <div className="d-flex gap-2 mt-2 flex-wrap">
                    <span className="badge bg-success">
                      Eng. Rate: {post.engagementRate}%
                    </span>
                    {post.ctr > 0 && (
                      <span className="badge bg-warning text-dark">
                        CTR: {post.ctr}%
                      </span>
                    )}
                    <span className="badge bg-info text-dark">
                      Total: {formatMetricNumber(post.engagement)} engagements
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {pagination.hasMore && (
            <div className="text-center">
              <button
                className="btn btn-outline-primary"
                onClick={() => loadPosts(true)}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </>
      )}

      <AppFooter
        buttons={[
          { icon: <FaArrowLeft />, label: 'Back', onClick: () => navigate(`/${tenant}/analytics`) },
          { icon: <FaHome />, label: 'Home', onClick: () => navigate(`/${tenant}/dashboard`) },
        ]}
      />
    </div>
  );
}

function MetricBadge({ icon, color, label, value }) {
  return (
    <div className="col-4 col-md-auto">
      <div className="d-flex align-items-center gap-1">
        <i className={`bi ${icon}`} style={{ color, fontSize: '0.75rem' }}></i>
        <small style={{ fontSize: '0.75rem' }}>
          <strong>{formatMetricNumber(value)}</strong>
          <span className="text-muted ms-1 d-none d-md-inline">{label}</span>
        </small>
      </div>
    </div>
  );
}

export default PostAnalytics;
